// Team Detail View Component
// Full-page view with Overview, Members, and Permissions tabs

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Users,
  Shield,
  Code,
  Settings,
  Package,
  ShieldCheck,
  Wrench,
} from 'lucide-react';
import { TeamOverviewTab } from './detail/TeamOverviewTab';
import { TeamMembersTab } from './detail/TeamMembersTab';
import { TeamPermissionsTab } from './detail/TeamPermissionsTab';
import { DeleteTeamModal } from './DeleteTeamModal';
import { userHasTeam } from '@/lib/utils';
import { useTeamSettingsPermissions } from '@/hooks/use-permissions';
import type { Team, User, Permission, TeamMember } from '@/types/teams';

const TEAM_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  users: Users,
  shield: Shield,
  code: Code,
  settings: Settings,
  package: Package,
  'shield-check': ShieldCheck,
  wrench: Wrench,
};

interface TeamDetailViewProps {
  team: Team;
  teams: Team[];
  users: User[];
  permissions: Permission[];
  onBack: () => void;
  onUpdate: (teamId: string, updates: Partial<Team>) => Promise<void>;
  onDelete: (teamId: string) => Promise<void>;
  onUpdatePermissions?: (teamId: string, grantedPermissions: string[]) => Promise<void>;
  onAddMembers?: (teamId: string, members: Array<Omit<TeamMember, 'assignedAt' | 'assignedBy'>>) => Promise<TeamMember[]>;
  onRemoveMembers?: (teamId: string, userIds: string[]) => Promise<boolean>;
  onRefresh?: () => void;
}

export function TeamDetailView({
  team,
  teams,
  users,
  permissions,
  onBack,
  onUpdate,
  onDelete,
  onUpdatePermissions,
  onAddMembers,
  onRemoveMembers,
  onRefresh,
}: Readonly<TeamDetailViewProps>) {
  const { canEditTeams, canDeleteTeams } = useTeamSettingsPermissions();
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editedTeam, setEditedTeam] = useState(team);
  const [hasPermissionChanges, setHasPermissionChanges] = useState(false);
  const [pendingPermissions, setPendingPermissions] = useState<string[]>([]);

  const IconComponent = TEAM_ICONS[team.icon] || Users;

  // Get team members - handle both string IDs and UserTeam objects
  const teamMembers = users.filter(u => userHasTeam(u.teams, team.id));

  const handleEdit = () => {
    setIsEditMode(true);
    setEditedTeam(team);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedTeam(team);
  };

  const handleSaveEdit = async () => {
    // If we have pending permission changes, save them via the dedicated API
    if (hasPermissionChanges && pendingPermissions.length > 0 && onUpdatePermissions) {
      await onUpdatePermissions(team.id, pendingPermissions);
    }
    // Exclude permissions from the update payload since they are saved via the dedicated API
    // This prevents the team update from overwriting the permissions that were just saved
    const { permissions: _permissions, ...teamWithoutPermissions } = editedTeam;
    await onUpdate(team.id, teamWithoutPermissions);
    setIsEditMode(false);
    setHasPermissionChanges(false);
    setPendingPermissions([]);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    await onDelete(team.id);
    setShowDeleteModal(false);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b bg-card p-6">
        {/* Team Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>

          <div
            className="h-16 w-16 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${team.color}20`, color: team.color }}
          >
            <IconComponent className="h-8 w-8" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold truncate">{team.name}</h1>
              {team.isDefault && (
                <Badge variant="outline">Default Team</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {teamMembers.length} {teamMembers.length === 1 ? 'member' : 'members'}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!isEditMode ? (
              <>
                {canEditTeams && (
                  <Button
                    variant="outline"
                    onClick={handleEdit}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
                {canDeleteTeams && team.canDelete && (
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                )}
              </>
            ) : (
              <>
                {hasPermissionChanges && activeTab === 'permissions' && (
                  <div className="flex items-center gap-2 mr-2 text-sm text-muted-foreground">
                    <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                    <span>Unsaved changes</span>
                  </div>
                )}
                <Button
                  variant="outline"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                >
                  Save Changes
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tab Bar - Enhanced Enterprise Design */}
      <div className="border-b bg-[rgb(242,245,245)] dark:bg-muted/30">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="h-auto p-0 bg-transparent border-0 gap-1">
            <TabsTrigger
              value="overview"
              className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="members"
              className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
            >
              Members
            </TabsTrigger>
            <TabsTrigger
              value="permissions"
              className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
            >
              Permissions
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'overview' && (
          <TeamOverviewTab
            team={isEditMode ? editedTeam : team}
            memberCount={teamMembers.length}
            isEditMode={isEditMode}
            onUpdate={(updates) => setEditedTeam({ ...editedTeam, ...updates })}
          />
        )}

        {activeTab === 'members' && (
          <TeamMembersTab
            team={team}
            teams={teams}
            users={users}
            members={teamMembers}
            isEditMode={isEditMode}
            onAddMembers={onAddMembers}
            onRemoveMembers={onRemoveMembers}
            onRefresh={onRefresh}
          />
        )}

        {activeTab === 'permissions' && (
          <TeamPermissionsTab
            team={isEditMode ? editedTeam : team}
            teams={teams}
            permissions={permissions}
            isEditMode={isEditMode}
            onPermissionsChange={(grantedPermissionIds) => {
              // Store the granted sub-permission IDs for saving via API
              setPendingPermissions(grantedPermissionIds);
              // Update the team's permissions based on the granted permission IDs
              const updatedPermissions = editedTeam.permissions.map(p => ({
                ...p,
                granted: p.permissionId ? grantedPermissionIds.includes(p.permissionId) : false,
              }));
              setEditedTeam({ ...editedTeam, permissions: updatedPermissions });
            }}
            onHasChanges={setHasPermissionChanges}
            onSave={handleSaveEdit}
            onCancel={handleCancelEdit}
          />
        )}
      </div>

      {/* Delete Team Modal */}
      {showDeleteModal && (
        <DeleteTeamModal
          team={team}
          members={teamMembers}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
}
